import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { fmtCVE, fmtDate, PAYMENT_METHODS } from "@/lib/format";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import {
  TrendUp, TrendDown, Warning, Clock, FileText, Receipt, Users, Plus, CircleNotch, CurrencyCircleDollar, Wallet,
} from "@phosphor-icons/react";

const ACTIONS_PT = { create: "Criado", update: "Atualizado", delete: "Eliminado", convert: "Convertido", login: "Entrada", quick_sale: "Venda rápida" };
const ENTITIES_PT = {
  invoice: "venda", quote: "orçamento", client: "cliente", product: "produto", payment: "pagamento", expense: "despesa",
  work: "obra", user: "utilizador", cash: "caixa", organization: "empresa", quote_request: "pedido de orçamento",
  reservation: "reserva", site_settings: "site", stock: "stock",
};
import { Button } from "@/components/ui/button";
import { RenewalBanner } from "@/components/RenewalBanner";

function Kpi({ icon: Icon, label, value, sub, tone = "default", testid }) {
  const tones = {
    default: "text-primary bg-accent",
    danger: "text-red-700 bg-red-100",
    warn: "text-amber-700 bg-amber-100",
    good: "text-emerald-700 bg-emerald-100",
  };
  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid={testid}>
      <div className={`mb-3 flex h-10 w-10 items-center justify-center rounded-lg ${tones[tone]}`}>
        <Icon size={20} weight="bold" />
      </div>
      <div className="text-2xl font-extrabold tracking-tight font-display">{value}</div>
      <div className="mt-0.5 text-sm text-muted-foreground">{label}</div>
      {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data)
    return (
      <div className="flex h-64 items-center justify-center">
        <CircleNotch size={28} className="animate-spin text-primary" />
      </div>
    );

  const actions = [
    { label: "Novo Orçamento", icon: FileText, to: "/orcamentos", testid: "quick-quote" },
    { label: "Nova venda", icon: Receipt, to: "/vendas", testid: "quick-invoice" },
    { label: "Novo Cliente", icon: Users, to: "/clientes", testid: "quick-client" },
    { label: "Novo Pagamento", icon: CurrencyCircleDollar, to: "/pagamentos", testid: "quick-payment" },
  ];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <RenewalBanner />
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Visão geral</p>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Painel</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          {actions.map((a) => (
            <Button key={a.to} variant="outline" size="sm" className="rounded-full" data-testid={a.testid}
              onClick={() => navigate(a.to)}>
              <a.icon size={16} className="mr-1.5" /> {a.label}
            </Button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Kpi testid="kpi-billing" icon={TrendUp} tone="default" label="Vendas do mês" value={fmtCVE(data.month_billing)} />
        <Kpi testid="kpi-expenses" icon={TrendDown} tone="warn" label="Despesas do mês" value={fmtCVE(data.month_expenses)} />
        <Kpi testid="kpi-profit" icon={Wallet} tone={data.month_profit < 0 ? "danger" : "good"} label="Lucro do mês" value={fmtCVE(data.month_profit)} />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Kpi testid="kpi-pending" icon={Clock} tone="warn" label="Documentos pendentes"
          value={data.pending_count} sub={fmtCVE(data.pending_amount)} />
        <Kpi testid="kpi-overdue" icon={Warning} tone="danger" label="Documentos vencidos"
          value={data.overdue_count} sub={fmtCVE(data.overdue_amount)} />
        <Kpi testid="kpi-quotes" icon={FileText} tone="default" label="Orçamentos em aberto"
          value={data.open_quotes_count} sub={fmtCVE(data.open_quotes_amount)} />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="rounded-xl border border-border bg-card p-5 lg:col-span-2" data-testid="billing-chart">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-display font-bold">Vendas (6 meses)</h3>
            <span className="text-sm font-semibold text-emerald-600">Recebido: {fmtCVE(data.total_received)}</span>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={data.chart} margin={{ left: -10, right: 10 }}>
              <defs>
                <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(var(--chart-1))" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="hsl(var(--chart-1))" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="month" tickLine={false} axisLine={false} fontSize={12} />
              <YAxis tickLine={false} axisLine={false} fontSize={11}
                tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)} />
              <Tooltip formatter={(v) => fmtCVE(v)} contentStyle={{ borderRadius: 8, border: "1px solid hsl(var(--border))" }} />
              <Area type="monotone" dataKey="valor" stroke="hsl(var(--chart-1))" strokeWidth={2.5} fill="url(#g)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-xl border border-border bg-card p-5" data-testid="recent-payments">
          <h3 className="mb-4 font-display font-bold">Pagamentos recentes</h3>
          <div className="space-y-3">
            {data.recent_payments.length === 0 && (
              <p className="text-sm text-muted-foreground">Sem pagamentos ainda.</p>
            )}
            {data.recent_payments.map((p, i) => (
              <div key={i} className="flex items-center justify-between border-b border-border/60 pb-2.5 last:border-0">
                <div>
                  <div className="text-sm font-medium">{p.invoice_number}</div>
                  <div className="text-xs text-muted-foreground">
                    {PAYMENT_METHODS[p.method] || p.method} · {fmtDate(p.date)}
                  </div>
                </div>
                <span className="text-sm font-semibold text-emerald-600">{fmtCVE(p.amount)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card p-5" data-testid="recent-activity">
        <h3 className="mb-4 font-display font-bold">Atividades recentes</h3>
        <div className="space-y-3">
          {data.activities.length === 0 && <p className="text-sm text-muted-foreground">Sem atividades.</p>}
          {data.activities.map((a, i) => (
            <div key={i} className="flex items-center gap-3 text-sm">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-accent text-primary">
                <Plus size={14} weight="bold" />
              </div>
              <div className="flex-1">
                <span className="font-medium">{ACTIONS_PT[a.action] || a.action}</span>{" "}
                <span className="text-muted-foreground">· {ENTITIES_PT[a.entity] || a.entity}</span>
                {a.user_name && <span className="text-muted-foreground"> · {a.user_name}</span>}
              </div>
              <span className="text-xs text-muted-foreground">{fmtDate(a.created_at)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
